import React from "react"
import { Col, Flex, Row, Tag, Typography } from "antd"
import { CalendarOutlined, DollarOutlined, PercentageOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useWizardContext } from "../../../WizardContext"
import styles from "./PreviewPricing.module.css"

const { Title, Text } = Typography

const formatPricingType = (type) => {
  if (type === "charge_per_person") return "Per Person"
  if (type === "charge_flat_rate") return "Flat Rate"
  return type
}

const formatFeeValue = (fee) =>
  fee.fee_type === "percentage" ? `${fee.price}%` : `$${fee.price}`

const AdjustmentCard = ({ adj }) => (
  <div className={styles.adjustmentCard}>
    <Flex justify="space-between" align="center" gap="small">
      <Text strong className={styles.adjName}>{adj.name}</Text>
      <Tag color={adj.rateChange?.startsWith("+") ? "green" : "red"} className={styles.rateTag}>
        {adj.rateChange}
      </Tag>
    </Flex>
    {(adj.appliesOnDisplay || adj.affectsDisplay) && (
      <Flex gap="middle" className={styles.adjMeta}>
        {adj.appliesOnDisplay && <Text className={styles.adjMetaText}>{adj.appliesOnDisplay}</Text>}
        {adj.affectsDisplay && <Text className={styles.adjMetaText}>{adj.affectsDisplay}</Text>}
      </Flex>
    )}
  </div>
)

AdjustmentCard.propTypes = {
  adj: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    rateChange: PropTypes.string,
    appliesOnDisplay: PropTypes.string,
    affectsDisplay: PropTypes.string
  }).isRequired
}

export const PreviewPricing = () => {
  const { form } = useWizardContext()
  const dayAdjustments = form.getFieldValue("dayAdjustments") || []
  const seasonalAdjustments = form.getFieldValue("seasonalAdjustments") || []
  const pricingFees = form.getFieldValue("pricingFees") || []
  const pricingType = form.getFieldValue("pricing_type")
  const depositPercentage = form.getFieldValue("deposit_percentage")
  const fullPaymentRequired = form.getFieldValue("full_payment_required")

  const hasData =
    dayAdjustments.length > 0 ||
    seasonalAdjustments.length > 0 ||
    pricingFees.length > 0 ||
    pricingType ||
    depositPercentage != null

  if (!hasData) return null

  const depositDisplay = fullPaymentRequired ? "Full payment required" : `${depositPercentage}%`

  return (
    <div className={styles.container}>
      <Title level={3} className={styles.title}>{"Pricing"}</Title>

      {(pricingType || depositPercentage != null) && (
        <Row gutter={[12, 12]} className={styles.metaRow}>
          {pricingType && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <DollarOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Pricing Type"}</Text>
                </Flex>
                <Text strong className={styles.metaValue}>{formatPricingType(pricingType)}</Text>
              </div>
            </Col>
          )}
          {depositPercentage != null && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <PercentageOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Deposit"}</Text>
                </Flex>
                <Text strong className={styles.metaValue}>{depositDisplay}</Text>
              </div>
            </Col>
          )}
        </Row>
      )}

      {dayAdjustments.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <CalendarOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Day Adjustments"}</Text>
          </Flex>
          <Flex vertical gap="small">
            {dayAdjustments.map((adj) => (
              <AdjustmentCard key={adj.id} adj={adj} />
            ))}
          </Flex>
        </div>
      )}

      {seasonalAdjustments.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <CalendarOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Seasonal Adjustments"}</Text>
          </Flex>
          <Flex vertical gap="small">
            {seasonalAdjustments.map((adj) => (
              <AdjustmentCard key={adj.id} adj={adj} />
            ))}
          </Flex>
        </div>
      )}

      {pricingFees.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <PercentageOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Taxes & Fees"}</Text>
          </Flex>
          <Row gutter={[8, 8]}>
            {pricingFees.map((fee) => (
              <Col key={fee.id} xs={24} sm={12}>
                <Flex justify="space-between" align="center" className={styles.feeRow}>
                  <Text className={styles.feeName}>{fee.title}</Text>
                  <Text strong className={styles.feeValue}>{formatFeeValue(fee)}</Text>
                </Flex>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </div>
  )
}
